import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTable, usePagination, useSortBy } from "react-table";

import cardMonth from "../elements/card-month";

// ---------- i18n ----------
const dict = {
    en: {
        // charts
        chartTitle: "System Usage (RAM, CPU, HDD)",
        chartTitleShort: "System Usage (RAM, CPU, HDD)",
        axisUsage: "Usage (%)",
        axisRamCpu: "RAM & CPU Usage (%)",
        axisHdd: "HDD Usage (%)",
        ram: "RAM",
        cpu: "CPU",
        hdd: "HDD",
        // tooltip
        tooltipRAM: "RAM",
        tooltipCPU: "CPU",
        tooltipHDD: "HDD",

        // table
        responseDate: "Response Date",
        show: "Show",
        entries: "entries",
        user: "User",
        pid: "PID",
        memory: "Memory",
        virtMem: "Virtual Memory Size",
        rss: "Resident set size",
        command: "Command",
        page: "Page",
        of: "of",

        // calendar (если cardMonth поддерживает язык — передайте при необходимости)
    },
    ru: {
        chartTitle: "Нагрузка системы (RAM, CPU, HDD)",
        chartTitleShort: "Нагрузка системы (RAM, CPU, HDD)",
        axisUsage: "Использование (%)",
        axisRamCpu: "Использование RAM и CPU (%)",
        axisHdd: "Использование HDD (%)",
        ram: "RAM",
        cpu: "CPU",
        hdd: "HDD",
        tooltipRAM: "RAM",
        tooltipCPU: "CPU",
        tooltipHDD: "HDD",

        responseDate: "Время ответа",
        show: "Показать",
        entries: "записей",
        user: "Пользователь",
        pid: "PID",
        memory: "Память",
        virtMem: "Размер виртуальной памяти",
        rss: "Резидентный набор",
        command: "Команда",
        page: "Стр.",
        of: "из",
    },
    ua: {
        chartTitle: "Навантаження системи (RAM, CPU, HDD)",
        chartTitleShort: "Навантаження системи (RAM, CPU, HDD)",
        axisUsage: "Використання (%)",
        axisRamCpu: "Використання RAM і CPU (%)",
        axisHdd: "Використання HDD (%)",
        ram: "RAM",
        cpu: "CPU",
        hdd: "HDD",
        tooltipRAM: "RAM",
        tooltipCPU: "CPU",
        tooltipHDD: "HDD",

        responseDate: "Час відповіді",
        show: "Показати",
        entries: "записів",
        user: "Користувач",
        pid: "PID",
        memory: "Пам’ять",
        virtMem: "Розмір віртуальної пам’яті",
        rss: "Резидентний набір",
        command: "Команда",
        page: "Стор.",
        of: "з",
    },
};

const getInitialLang = () =>
    (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- calendar ----------
const Calendar = ({ monitoringTotal = [], selectedDate, onSelectDate }) => {
    const now = new Date();
    const months = [-1, 0, 1].map((offset) => {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const totalsByDate = React.useMemo(() => {
        const map = {};
        (monitoringTotal || []).forEach((item) => {
            if (!item.dateKey) return;
            map[item.dateKey] = item;
        });
        return map;
    }, [monitoringTotal]);

    const colorResolver = (dateKey) => {
        const item = totalsByDate[dateKey];
        if (!item) return { color: "#9e9e9e", title: "No data" };
        if (item.score >= 95) return { color: "#8bc34a", title: `${item.score}% healthy` };
        if (item.score >= 70) return { color: "#ffcc00", title: `${item.score}% warning` };
        return { color: "#f44336", title: `${item.score}% critical` };
    };

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
            }}
        >
            {months.map(({ year, month }) => (
                <React.Fragment key={`${year}-${month}`}>
                    {cardMonth(year, month, colorResolver, onSelectDate, selectedDate)}
                </React.Fragment>
            ))}
        </div>
    );
};

// ---------- main monitor ----------
const SystemMonitor = ({ t, details = {} }) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [chartData, setChartData] = useState({ RAM: [], CPU: [], HDD: [] });
    const [tableData, setTableData] = useState([]);
    const [pageSize, setPageSize] = useState(10);

    const normalizeSeries = (arr) => {
        if (!Array.isArray(arr)) return null;
        const mapped = arr
            .map((p) => {
                const ts = p.date || p.created || p.ts || p.time || p.valid_date;
                const val = p.value ?? p.usage ?? p.cpu ?? p.ram ?? p.hdd;
                const x = ts ? new Date(ts).getTime() : null;
                const y = typeof val === "string" ? parseFloat(val) : val;
                return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
            })
            .filter(Boolean)
            .sort((a, b) => a[0] - b[0]);
        return mapped.length ? mapped : null;
    };

    useEffect(() => {
        const ramSeries =
            normalizeSeries(details.ram_history) ||
            normalizeSeries(details.ram_history_month) ||
            [];
        const cpuSeries =
            normalizeSeries(details.cpu_history) ||
            normalizeSeries(details.cpu_history_month) ||
            [];
        const hddSeries =
            normalizeSeries(details.hdd_history) ||
            normalizeSeries(details.hdd_history_month) ||
            [];

        setChartData({
            RAM: ramSeries || [],
            CPU: cpuSeries || [],
            HDD: hddSeries || [],
        });
    }, [details]);

    useEffect(() => {
        const source =
            (Array.isArray(details.process_history) && details.process_history) ||
            (Array.isArray(details.process_usage) && details.process_usage) ||
            [];

        const mapped = source
            .map((item, idx) => {
                const tested = item.time || item.date || item.created || item.created_at || item.ts;
                const testedDate = tested ? new Date(tested) : null;
                const testedKey =
                    testedDate && !Number.isNaN(testedDate.getTime())
                        ? testedDate.toISOString().slice(0, 10)
                        : null;

                const cpuValRaw =
                    item.cpu ??
                    item.cpuVal ??
                    item.cpu_usage ??
                    item.cpu_percent ??
                    item.cpu_percent_usage ??
                    item.value;
                const memValRaw =
                    item.mem ??
                    item.memVal ??
                    item.memory ??
                    item.mem_usage ??
                    item.mem_percent ??
                    item.memory_percent ??
                    item.mem_usage_percent;

                return {
                    user: item.user || item.username || item.owner || "",
                    pid: item.pid || item.process_id || item.id || idx,
                    cpuVal: Number.parseFloat(cpuValRaw) || 0,
                    memVal: Number.parseFloat(memValRaw) || 0,
                    virtualMemory:
                        item.virtualMemory ||
                        item.vmem ||
                        item.virtual_memory ||
                        item.virtual_memory_size ||
                        "",
                    residentSet:
                        item.residentSet ||
                        item.rss ||
                        item.resident_set ||
                        item.resident_set_size ||
                        "",
                    command: item.command || item.cmd || item.process || item.name || "",
                    tested: tested || null,
                    testedKey,
                };
            })
            .filter(Boolean);

        setTableData(mapped);
        setSelectedDate(null);
    }, [details]);

    // колонки таблицы
    const columns = React.useMemo(
        () => [
            { Header: t.user, accessor: "user" },
            { Header: t.pid, accessor: "pid" },
            { Header: t.cpu, accessor: "cpuVal", Cell: ({ value }) => `${value.toFixed(2)}%` },
            { Header: t.memory, accessor: "memVal", Cell: ({ value }) => `${value.toFixed(2)}%` },
            { Header: t.virtMem, accessor: "virtualMemory" },
            { Header: t.rss, accessor: "residentSet" },
            { Header: t.command, accessor: "command" },
        ],
        [t]
    );

    const handlePointClick = (event) => {
        const dateKey = new Date(event.point.x).toISOString().slice(0, 10);
        setSelectedDate(dateKey);
    };

    // опции графика
    const chartOptions = {
        chart: { type: "line", zoomType: "x" },
        title: { text: t.chartTitle },
        xAxis: { type: "datetime" },
        yAxis: [{ title: { text: t.axisUsage } }],
        tooltip: {
            shared: true,
            formatter: function () {
                const dateStr = Highcharts.dateFormat("%Y-%m-%d %H:%M:%S", this.x);
                const rows = (this.points || [])
                    .map((p) => `${p.series.name}: ${p.y?.toFixed ? p.y.toFixed(2) : p.y}%`)
                    .join("<br/>");
                return `<b>${dateStr}</b><br/>${rows}`;
            },
        },
        series: [
            {
                name: t.ram,
                data: chartData.RAM,
                color: "#1f78b4",
                point: { events: { click: handlePointClick } },
            },
            {
                name: t.cpu,
                data: chartData.CPU,
                color: "#e31a1c",
                point: { events: { click: handlePointClick } },
            },
            {
                name: t.hdd,
                data: chartData.HDD,
                color: "#33a02c",
                point: { events: { click: handlePointClick } },
            },
        ],
    };

    // таблица: сортировка и пагинация
    const filteredData = React.useMemo(() => {
        if (!selectedDate) return tableData;
        return tableData.filter((row) => row.testedKey === selectedDate);
    }, [tableData, selectedDate]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize: updatePageSize,
        state: { pageIndex },
    } = useTable(
        { columns, data: filteredData, initialState: { pageSize } },
        useSortBy,
        usePagination
    );

    const calendarData = React.useMemo(() => {
        // если пришёл monitoring_total за месяц из details
        if (Array.isArray(details.monitoring_total) && details.monitoring_total.length) {
            return details.monitoring_total
                .map((item) => {
                    const key = (() => {
                        if (item.dateKey) return item.dateKey;
                        if (item.valid_date) {
                            const d = new Date(item.valid_date);
                            if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
                        }
                        return null;
                    })();
                    if (!key) return null;
                    const scoreRaw = item.uptime || item.score || item.value;
                    let score = Number.parseFloat(String(scoreRaw).replace("%", "")) || 0;
                    if (!score && (item.success_request_qty || item.fail_requests_qty)) {
                        const succ = Number(item.success_request_qty) || 0;
                        const fail = Number(item.fail_requests_qty) || 0;
                        const total = succ + fail;
                        if (total > 0) score = (succ / total) * 100;
                    }
                    return { dateKey: key, score };
                })
                .filter(Boolean);
        }

        // иначе считаем по данным таблицы (как прежде)
        const map = {};
        tableData.forEach((row) => {
            const key = row.testedKey;
            if (!key) return;
            const list = map[key] || [];
            list.push(row);
            map[key] = list;
        });
        return Object.entries(map).map(([dateKey, list]) => {
            const avgCpu = list.reduce((sum, r) => sum + (r.cpuVal || 0), 0) / list.length;
            const score = Math.max(0, Math.min(100, 100 - avgCpu));
            return { dateKey, score: Number.isFinite(score) ? score : 0 };
        });
    }, [tableData, details.monitoring_total]);

    return (
        <div style={{ padding: "20px" }}>
            <Calendar
                monitoringTotal={calendarData}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
            />
            <div style={{ marginBottom: "20px" }}>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            </div>

            <div>
                <h4>
                    {t.responseDate}: {new Date().toLocaleString()}
                </h4>

                <div style={{ marginBottom: "10px" }}>
                    <label>
                        {t.show}{" "}
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setPageSize(val);
                                updatePageSize(val);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>{" "}
                        {t.entries}
                    </label>
                </div>

                <table
                    {...getTableProps()}
                    style={{ borderCollapse: "collapse", width: "100%", marginTop: "20px" }}
                >
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    style={{
                                        border: "1px solid #ccc",
                                        padding: "8px",
                                        textAlign: "left",
                                        background: "#f4f4f4",
                                    }}
                                >
                                    {column.render("Header")}
                                    <span>
                      {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => (
                                    <td
                                        {...cell.getCellProps()}
                                        style={{ border: "1px solid #ccc", padding: "8px" }}
                                    >
                                        {cell.render("Cell")}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>

                <div style={{ marginTop: "10px", textAlign: "center" }}>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {"<<"}
                    </button>{" "}
                    <button onClick={previousPage} disabled={!canPreviousPage}>
                        {"<"}
                    </button>{" "}
                    <span>
            {t.page} <strong>{pageIndex + 1} {t.of} {pageOptions.length}</strong>{" "}
          </span>
                    <button onClick={nextPage} disabled={!canNextPage}>
                        {">"}
                    </button>{" "}
                    <button
                        onClick={() => gotoPage(pageOptions.length - 1)}
                        disabled={!canNextPage}
                    >
                        {">>"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- page ----------
const Process = ({ lang: langProp, details }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    return <SystemMonitor t={t} details={details} />;
};

export default Process;
